import {
  dateOfWeek,
  permissions,
  remCss,
  rgba,
  scssVariables,
  userFullName,
  useScreenDetail
} from 'src/helpers';
import React from 'react';
import SVGIcon, { BASE_ICONS, GENERAL_ICONS } from 'src/components/svg';
import model from './model';
import moment from 'moment/moment';
import { Avatar, MCButton, MCText, View } from 'src/components/basic';
import LANG_CONST from 'src/helpers/lang/const';
import { popupModel } from 'components/complex';
import slotScheduleModel, { SlotSchedule } from 'components/slotSchedule';

const heightOfHour = 234;
const heightOfMinute = heightOfHour / 60;

const EmptySlot = ({ emptyHeight = 0, addHeight = 0, canAdd = true, addSlotInHour, style }) => {
  if (!emptyHeight && !addHeight) return null;

  if (!canAdd || !addHeight)
    return (
      <View
        style={{
          minHeight: remCss(emptyHeight + addHeight),
          maxHeight: remCss(emptyHeight + addHeight)
        }}
      />
    );

  return (
    <>
      <View style={{ minHeight: remCss(emptyHeight), maxHeight: remCss(emptyHeight) }} />
      <View
        style={{
          minHeight: remCss(addHeight),
          maxHeight: remCss(addHeight),
          overflow: 'hidden',
          ...style
        }}
        className={'mc-add-slot'}
      >
        <MCButton
          type={'button'}
          onClick={addSlotInHour}
          style={{ borderWidth: remCss(2), borderStyle: 'dashed' }}
          flex
          radius
          tiny
          iconname={BASE_ICONS.PLUS}
          label={LANG_CONST.buttons.add_slot}
          outline
          tertiary
        >
          <View
            style={{
              gap: remCss(5),
              flexWrap: 'wrap',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {addHeight > 20 && (
              <>
                <SVGIcon iconname={BASE_ICONS.PLUS} width={20} />
                <MCText
                  style={{ color: null }}
                  size={18}
                  lineHeight={18}
                  flex
                  label={LANG_CONST.buttons.add_slot}
                />
              </>
            )}
          </View>
        </MCButton>
      </View>
    </>
  );
};

const Slot = ({ item, canEdit = true, height, disabled, onClick }) => {
  const hourMinuteStart = moment(item.startDate).format('HH:mm');
  const hourMinuteEnd = moment(item.endDate).format('HH:mm');
  const screenDetail = useScreenDetail();
  const isSmallScreen = screenDetail.innerWidth <= 1024;

  const location = !!item.hospital && (
    <MCText size={14} lineHeight={15} numberOfLines={1} textLight>
      {item.hospital?.split('_')[0]}
    </MCText>
  );

  const delBtn = canEdit && (
    <MCButton
      circle
      style={{ marginLeft: 'auto', marginRight: remCss(-3), marginTop: remCss(-3) }}
      padding={0}
      primary={item.enabled !== false && !item.patientId}
      quaternary={item.enabled !== false && item.patientId}
      tertiary={item.enabled === false}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        slotScheduleModel.removeSlot(item, model.input('doctor').id || 0, (err, data, success) => {
          if (success) {
            model.setState({
              weekSlots: model.state.weekSlots.filter((slot) => slot.id !== item.id)
            });
          }
        });
      }}
      width={16}
      iconname={BASE_ICONS.CLOSE}
    />
  );

  const virtualColor = scssVariables['colors-mc-primary'];
  const officeColor = scssVariables['colors-mc-orange'];

  let context;

  if (item.patientId) {
    context = (
      <View style={{ flexDirection: 'row', gap: remCss(5) }}>
        <Avatar
          text={isSmallScreen ? item.firstName[0] : userFullName(item)}
          width={16}
          height={16}
          image={item.avatar}
        />
        <MCText
          size={14}
          lineHeight={14}
          weight={500}
          numberOfLines={1}
          flex
          style={{ marginTop: 'auto' }}
        >
          {userFullName(item)}
        </MCText>
      </View>
    );
  } else {
    context = item.appointmentTypes
      .sort((a, b) => (a.length < b.length ? 1 : -1))
      .map((type) => (
        <div style={{ backgroundColor: 'white', borderRadius: remCss(8), minWidth: 0 }} key={type}>
          <MCText
            heading
            label={LANG_CONST.constants.appointment_types[type]}
            numberOfLines={1}
            style={{
              backgroundColor:
                item.enabled === false
                  ? rgba(scssVariables['colors-mc-heading'], 0.05)
                  : type === 'virtual'
                    ? rgba(virtualColor, 0.3)
                    : rgba(officeColor, 0.2),
              paddingHorizontal: remCss(8),
              borderRadius: remCss(8)
            }}
            size={14}
            lineHeight={18}
            weight={400}
          />
        </div>
      ));
  }

  return (
    <View
      onClick={!disabled && onClick}
      style={{
        minHeight: remCss(height),
        maxHeight: remCss(height),
        flexDirection: 'row',
        overflow: 'hidden',
        border: '2px solid transparent',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        width: '100%',
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        maxWidth: (screenDetail.innerWidth - 130) / 7
      }}
    >
      <View
        style={{
          borderRadius: remCss(5),
          width: '100%',
          padding: remCss(8),
          gap: remCss(3),
          backgroundColor:
            item.enabled === false
              ? '#fff'
              : !item.appointmentId
                ? rgba(scssVariables['colors-mc-primary'], 0.12)
                : rgba(scssVariables['colors-mc-quaternary'], 0.12),
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: item.enabled === false ? scssVariables['colors-mc-table-th'] : 'transparent',
          boxSizing: 'border-box'
        }}
      >
        <View style={{ flexDirection: 'row', gap: remCss(6.5), marginBottom: remCss(3) }}>
          {context}
          {delBtn}
        </View>
        <MCText size={14} lineHeight={14} style={{ color: null }} numberOfLines={1}>
          {hourMinuteStart + '-' + hourMinuteEnd}
        </MCText>
        {item.appointmentTypes.includes('in_office') && location}
      </View>
    </View>
  );
};

const checkOverHourCell = (dateMoment, hour, maxHour) => {
  const over =
    dateMoment.format('HH:mm') > moment().hours(hour).add(1, 'hour').minutes(0).format('HH:mm');

  if (over && maxHour) {
    return dateMoment.format('HH') <= maxHour.toString();
  }

  return over;
};

const paddingCell = 0;
export const ScheduleCalendar = ({ selectedDate, hours = [] }) => {
  model.use();

  const openModifySlots = (date, slot, addHour) => {
    if (!slotScheduleModel.checkDoctorSelected(model.state.doctor)) return;

    popupModel.setActivePopup('SlotSchedule', {
      active: true,
      component: SlotSchedule,
      afterHide: () => {
        model.selectDay(model.state.selectedDate, true);
      },
      props: {
        selectedDay: date.valueOf(),
        doctorId: model.input('doctor').id || 0,
        slot,
        addHour
      }
    });
  };

  let startDateOfWeek = selectedDate.clone().startOf('isoWeek');

  // table has 8 columns
  // init first column
  let cells = hours.map((hour) => {
    return [
      <td className={'mc-col-header'} key={'header_' + hour}>
        {hour + ':00'}
      </td>
    ];
  });

  // for rest 7 columns
  dateOfWeek.forEach((_, i) => {
    const colIndex = i + 1;
    let preEndMoment = null;
    // for row
    hours.forEach((hour, j) => {
      const date = startDateOfWeek.clone().add(i, 'days');
      const filterByDateHour = model.filterByDateHour(date, hour);

      const slots = filterByDateHour.map((item, i) => {
        const currentMinutes = Number(moment(item.startDate).format('mm'));
        const endMoment = moment(item.endDate);

        preEndMoment = i === 0 ? preEndMoment : moment(filterByDateHour[i - 1].endDate);

        let emptyDuration = 0,
          addDuration = currentMinutes;

        if (preEndMoment?.format('HH') === hour) {
          if (i === 0) emptyDuration = Number(preEndMoment.format('mm'));
          addDuration = currentMinutes - Number(preEndMoment.format('mm'));
        }

        // duration in minutes
        const duration = (item.endDate - item.startDate) / 60000;

        // slot size in px => 10px per 5 minutes
        const slotSize =
          duration * heightOfMinute +
          (checkOverHourCell(moment(item.endDate), hour)
            ? paddingCell + (endMoment.hour() - Number(hour) + 1)
            : 0);

        const canModify = slotScheduleModel.canModify(item);

        return (
          <React.Fragment key={hour + '_' + i}>
            <EmptySlot
              canAdd={permissions.ACTIONS.SLOT_ADD_BUTTON.checkActive({
                auth: model.state.user
              })}
              addSlotInHour={() => openModifySlots(date, null, Number(hour))}
              emptyHeight={emptyDuration * heightOfMinute}
              addHeight={addDuration * heightOfMinute}
            />
            <Slot
              item={item}
              height={slotSize}
              canEdit={canModify}
              onClick={() => openModifySlots(date, item)}
              date={date}
              hour={hour}
            />
          </React.Fragment>
        );
      });
      preEndMoment = !filterByDateHour.length
        ? preEndMoment
        : moment(filterByDateHour[filterByDateHour.length - 1].endDate);

      let emptyDuration = 0,
        addDuration = 60;

      if (preEndMoment?.format('HH') === hour) {
        emptyDuration = filterByDateHour.length ? 0 : Number(preEndMoment.format('mm'));
        addDuration = 60 - Number(preEndMoment.format('mm'));
      }
      if (preEndMoment?.format('HH') > hour) {
        addDuration = 0;
      }

      cells[j][colIndex] = (
        <td
          key={date.format('YYYYMMDD') + '_' + hour}
          // className={pastClassName}
        >
          <View style={{ padding: paddingCell, height: remCss(heightOfHour) }}>
            {slots}
            <EmptySlot
              canAdd={permissions.ACTIONS.SLOT_ADD_BUTTON.checkActive({
                auth: model.state.user
              })}
              emptyHeight={emptyDuration * heightOfMinute}
              addHeight={addDuration * heightOfMinute}
              addSlotInHour={() => openModifySlots(date, null, Number(hour))}
            />
          </View>
        </td>
      );
    });
  });

  return (
    <table data-testid='schedule-table' className={'mc-main-content-box mc-schedule-table'}>
      <colgroup>
        <col span='1' width={remCss(80)} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
        <col span='1' width={'13%'} />
      </colgroup>
      <thead>
        <tr>
          <th className={'mc-col-header'}>
            <SVGIcon iconname={GENERAL_ICONS.CLOCK} />
          </th>
          {dateOfWeek.map((v, i) => {
            const date = startDateOfWeek.clone().add(i, 'days');
            return (
              <th
                key={v}
                className={`mc-header mc-pointer mc-bg-hover ${selectedDate.isSame(date, 'day') ? ' selected' : ''}`}
                onClick={() => openModifySlots(date)}
              >
                <div>{date.format('DD')}</div>
                <div className={'mc-header-note'}>{v}</div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {cells.map((row, i) => (
          <tr data-testid='schedule-row' key={i}>
            {row}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
